import { useEffect, useState } from "react";
import { RadioButton } from "@/components/common/radio-button";
import { cn } from "@/lib/utils";
import { MoreActionItem, MoreActionPopover } from "@/components/common/more-action-popover";
import { DownloadIcon, MoreIcon, TrashIcon } from "@/components/ui/icon";
import { useTranslation } from "react-i18next";
import { EditIcon } from "@/components/agent-dashboard/assets";
import { TextareaEditable } from "@/components/common/textarea-editable";
import { DeleteConfirmModal } from "@/components/ui/modal";
import { Image } from "@/components/common/image";
import folderBg from '@/assets/assets/folder-bg.webp';
import folderBgHover from '@/assets/assets/folder-bg-hover.webp';
import { formatDateSmart } from "@/utils/date-formatter";

interface MaterialCardProps {
  id: number;
  name: string;
  time: number;
  /** 是否开启选择 */
  openSelected?: boolean;
  /** 是否选中 */
  isSelected?: boolean;
  /** 是否开启编辑名称 */
  isEditName?: boolean;
  /** 封面图片 */
  cover?: string;
  /** 关联智能体类型标签（已翻译文案） */
  tags?: string[];
  /** 选择状态改变回调 */
  onSelected?: (selected: { id: number; checked: boolean }) => void;
  /** 删除回调 */
  onDelete?: (id: number) => void;
  /** 下载回调 */
  onDownload?: (id: number) => void;
  /** 重命名回调 */
  onRename?: (id: number, name: string) => void;
  /** 详情回调 */
  onDetail?: (id: number) => void;
}

/** 素材卡片 */
export const MaterialCard = ({
  id,
  name,
  time,
  cover,
  tags,
  openSelected,
  isSelected,
  isEditName,
  onSelected,
  onDelete,
  onDownload,
  onRename,
  onDetail,
}: MaterialCardProps) => {
  const { t } = useTranslation();
  const [checked, setChecked] = useState(isSelected || false);
  // 添加状态来控制TextareaEditable的编辑状态
  const [isEditingName, setIsEditingName] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [hoverFolder, setHoverFolder] = useState(false);

  useEffect(() => {
    setIsEditingName(isEditName || false);
  }, [isEditName]);

  useEffect(() => {
    setChecked(isSelected || false);
  }, [isSelected]);

  const handleSelect = (value: boolean, id: number) => {
    setChecked(value);
    onSelected?.({ id, checked: value });
  }

  const handleRename = (newName: string) => {
    onRename?.(id, newName);
    // 重命名完成后关闭编辑状态
    setIsEditingName(false);
  };

  // 处理编辑状态变化
  const handleEditingChange = (editing: boolean) => {
    setIsEditingName(editing);
  };

  // 开始重命名操作
  const startRename = () => {
    setIsEditingName(true);
  };

  return (
    <div
      material-card
      className="w-full h-full relative"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={cn(
          "group w-full h-full rounded-[12px] group px-1 pt-2 transition-colors select-none cursor-pointer",
          "hover:bg-[rgba(234,236,240,1)]",
          checked && "bg-[rgba(234,236,240,1)]",
        )}
        onMouseEnter={() => setHoverFolder(true)}
        onMouseLeave={() => setHoverFolder(false)}
        onClick={() => {
          if (openSelected) {
            handleSelect(!checked, id);
          }
        }}
      >
        <div className={cn("relative w-full rounded-[12px] transition-all select-none px-4 py-2")}>
          <div
            className='relative overflow-hidden flex-shrink-0'
            onClick={(e) => {
              if (openSelected) return;
              e.stopPropagation();
              onDetail?.(id);
            }}
          >
            <Image src={(!isPopoverOpen && (hoverFolder || checked)) ? folderBgHover : folderBg} className="size-full object-cover" />
            {tags && tags.length > 0 && (
              <div className="pointer-events-none absolute left-2 top-2 z-[5] flex flex-wrap gap-1.5">
                {tags.map((label, i) => (
                  <span
                    key={`${label}-${i}`}
                    className="rounded-[4px] bg-[rgba(255,255,255,1)] px-1.5 py-0.5 text-[10px] text-[rgba(71,84,103,1)]"
                  >
                    {label}
                  </span>
                ))}
              </div>
            )}
            {cover && (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-full overflow-hidden flex items-center justify-center py-[7px] px-[6.8px] pt-7">
                <Image src={cover} className="max-h-full max-w-full object-contain rounded-[6px] border border-[rgba(0,0,0,0.1)]" loading="lazy" />
              </div>
            )}
          </div>
          <RadioButton
            checked={checked}
            onChange={(value) => handleSelect(value, id)}
            className={cn(
              "absolute size-6 top-1 left-1 rounded-[5px] border-[3px] border-white bg-[rgba(0,0,0,0.25)]",
              "opacity-0 group-hover:opacity-100",
              checked && "opacity-100 bg-[rgba(90,92,96,1)]",
            )}
          />
        </div>

        {/* 更多操作 */}
        <div
          className="opacity-0 group-hover:opacity-100 absolute top-3 right-2"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreActionPopover
            className="w-40"
            side="bottom"
            onOpenChange={setIsPopoverOpen}
            tiggerIcon={
              <MoreIcon className="size-5 text-black" />
            }
            tiggerClassName="hover:bg-white/60 bg-white/60 shadow-sm"
          >
            <MoreActionItem
              icon={<EditIcon className="w-4 h-4" />}
              label={t("common.rename")}
              className='text-[rgba(16,24,40,1)] font-medium'
              onClick={() => startRename()}
            />
            {/* <MoreActionItem
              icon={<DownloadIcon className="w-4 h-4" />}
              label={t("common.download")}
              className='text-[rgba(16,24,40,1)] font-medium'
              onClick={() => onDownload?.(id)}
            /> */}
            <DeleteConfirmModal
              trigger={
                <MoreActionItem
                  icon={<TrashIcon className="w-4 h-4" />}
                  className='text-[rgba(16,24,40,1)] font-medium'
                  label={t("common.delete")}
                />
              }
              onConfirm={() => onDelete?.(id)}
            />
          </MoreActionPopover>
        </div>
      </div>
      <div
        className="w-full absolute -bottom-2 translate-y-full"
        onClick={(e) => e.stopPropagation()}
      >
        <TextareaEditable
          value={name}
          onChange={handleRename}
          maxRows={3}
          textareaClassName="max-h-16"
          textClassName="text-base text-[rgba(16,24,40,1)] font-medium"
          isEditing={isEditingName}
          onEditingChange={handleEditingChange}
          maxLength={20}
        />
        <div className="w-full text-center pt-0.5 text-xs text-[rgba(102,112,133,1)]">
          {formatDateSmart(time * 1000).replace(
            "Today",
            t("creation_folder.project_card.today")
          )}
        </div>
      </div>
    </div>
  )
}
